import { PrerequisiteDiff } from './PrerequisiteDiff.js';

// Pipeline/plumbing keys that carry no meaning for the academic reviewer — hidden from the patch
// preview so the reviewer sees only content-bearing changes (AD feedback R-10). They still apply at
// execution time; this only affects what is displayed.
const HIDDEN_PATCH_KEYS: ReadonlySet<string> = new Set([
  'groupId',
  'parentId',
  'ordinal',
  'itemTypeSlug',
  'status',
  'tenantId',
]);

export function PatchPreview({
  patch,
  courseId,
  entityId,
  highlightChanges = false,
}: {
  patch: Record<string, unknown>;
  courseId?: string;
  entityId?: number | null;
  highlightChanges?: boolean;
}) {
  // Prerequisite (concept-map edge) changes get a dedicated human-readable view; everything
  // else falls back to the raw JSON diff below.
  const prereq = patch.prerequisites;
  const hasPrereq = prereq !== undefined;
  const prereqTuple = isDiffTuple(prereq) ? prereq : undefined;

  // The `_edits` envelope. Without this branch it would fall through to the loop below, where
  // `isDiffTuple` is false for an array — so a whole envelope would render as ONE inert
  // `~ _edits: [{…}]` **context** line: on screen, coloured as unchanged, and unreadable. That is the
  // same failure the nested-tuple rule exists to prevent (a reviewer who cannot see the change cannot
  // approve it), which is why the envelope could not ship without this.
  const edits = isEditsEnvelope(patch._edits) ? patch._edits : undefined;

  const lines: Array<{ type: 'remove' | 'add' | 'context'; text: string }> = [];
  for (const [key, value] of Object.entries(patch)) {
    if (key === '_edits' && edits) {
      continue; // rendered as per-field diffs below
    }
    if (key === 'prerequisites') {
      continue; // rendered by PrerequisiteDiff
    }
    if (HIDDEN_PATCH_KEYS.has(key)) {
      continue; // backend-only plumbing — not shown to the reviewer
    }
    if (isDiffTuple(value)) {
      lines.push(
        { type: 'remove', text: `- ${key}: ${stringifyValue(value.before)}` },
        { type: 'add', text: `+ ${key}: ${stringifyValue(value.after)}` },
      );
      continue;
    }
    lines.push({ type: 'context', text: `~ ${key}: ${stringifyValue(value)}` });
  }

  return (
    <div className="flex flex-col gap-3">
      {edits ? (
        <div className="flex flex-col gap-2" data-testid="edits-envelope">
          {edits.map((edit, index) => (
            <div className="rounded-xl border border-slate-800 bg-slate-950 p-4" key={`edit-${index}`}>
              <div className="flex flex-wrap items-center gap-2 pb-2 text-[11px] uppercase tracking-wide text-slate-400">
                <span className="font-semibold text-slate-200">{describeLocator(edit.locator)}</span>
                {/*
                  Fragment vs whole is shown because the two mean materially different things to a
                  reviewer: "this replaces one sentence inside the prompt" is not the same promise as
                  "this replaces the prompt".
                */}
                <span className="rounded bg-slate-800 px-1.5 py-0.5 text-slate-300">
                  {edit.mode === 'whole' ? 'replaces whole field' : 'replaces text within field'}
                </span>
              </div>
              <pre className="whitespace-pre-wrap break-words text-[11px] leading-5">
                <FieldTextDiff before={edit.before} after={edit.after} highlightChanges={highlightChanges} />
              </pre>
              {edit.rationale ? (
                <p className="pt-2 text-[11px] italic leading-4 text-slate-400">{edit.rationale}</p>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}
      {hasPrereq ? (
        <PrerequisiteDiff
          after={prereqTuple ? prereqTuple.after : prereq}
          before={prereqTuple ? prereqTuple.before : undefined}
          courseId={courseId}
          entityId={entityId}
        />
      ) : null}
      {lines.length > 0 ? (
        <pre className="rounded-xl border border-slate-800 bg-slate-950 p-4 text-[11px] leading-5 text-slate-200">
          {lines.map((line, index) => (
            <div
              className={
                line.type === 'remove'
                  ? 'text-rose-300'
                  : line.type === 'add'
                    ? 'text-emerald-300'
                    : 'text-slate-400'
              }
              key={`${line.text}-${index}`}
            >
              {line.text}
            </div>
          ))}
        </pre>
      ) : null}
    </div>
  );
}

function stringifyValue(value: unknown) {
  return JSON.stringify(value);
}

function isDiffTuple(value: unknown): value is { before: unknown; after: unknown } {
  return !!value && typeof value === 'object' && !Array.isArray(value) && 'before' in value && 'after' in value;
}

interface EditsEntry {
  locator?: { scope?: string; id?: number; field?: string };
  before: unknown;
  after: unknown;
  mode?: string;
  rationale?: string;
}

/**
 * Deliberately permissive about everything except the two fields this component RENDERS. The API
 * refuses a malformed envelope at three chokepoints, so the preview's job is not to re-validate — it
 * is to make sure that anything carrying `before`/`after` is shown as a diff rather than silently
 * falling through to the inert context line.
 */
function isEditsEnvelope(value: unknown): value is EditsEntry[] {
  return (
    Array.isArray(value)
    && value.length > 0
    && value.every((entry) => !!entry && typeof entry === 'object' && 'before' in entry && 'after' in entry)
  );
}

/** `step 12 · prompt` — the form the fix specs themselves use ("task 17010 step 3 prompt"). */
export function describeLocator(locator: EditsEntry['locator']) {
  if (!locator || typeof locator !== 'object') {
    return 'unknown location';
  }
  const scope = typeof locator.scope === 'string' ? locator.scope : 'field';
  const field = typeof locator.field === 'string' ? locator.field : '?';
  return locator.id === undefined ? `${field}` : `${scope} ${locator.id} · ${field}`;
}

/** Keep unchanged context visible while marking the exact changed span in a whole field. */
function FieldTextDiff({ before, after, highlightChanges }: { before: unknown; after: unknown; highlightChanges: boolean }) {
  if (!highlightChanges || typeof before !== 'string' || typeof after !== 'string') return <>
    <div className="text-rose-300">- {stringifyValue(before)}</div>
    <div className="text-emerald-300">+ {stringifyValue(after)}</div>
  </>;
  let prefix = 0, suffix = 0;
  while (prefix < before.length && prefix < after.length && before[prefix] === after[prefix]) prefix++;
  while (suffix < before.length - prefix && suffix < after.length - prefix
    && before[before.length - suffix - 1] === after[after.length - suffix - 1]) suffix++;
  const side = (text: string, changed: 'del' | 'ins') => {
    const end = text.length - suffix;
    return <>{text.slice(0, prefix)}{changed === 'del'
      ? <del className="bg-rose-900 font-bold">{text.slice(prefix, end)}</del>
      : <ins className="bg-emerald-900 font-bold">{text.slice(prefix, end)}</ins>}{text.slice(end)}</>;
  };
  return <>
    <div className="text-rose-300">- "{side(before, 'del')}"</div>
    <div className="text-emerald-300">+ "{side(after, 'ins')}"</div>
  </>;
}
